import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "../../../../services/request";
import {
  base64ToFile,
  getBusinessBoxPath,
  getUserProfilePath,
  getWheelUploadPath,
  handleFileUpload,
} from "../../../../utils/fileUpload";
import {
  fetchImageAsBase64,
  getLoggedInUser,
  getRandomId,
  isBase64Image,
} from "../../../../utils/helpers";
import Button from "../../../Button";
import Input from "../../../Input";
import TextView from "../../../TextView";
import { getDocumentId, isDocumentValid } from "./helpers";
import { useSelector } from "react-redux";
import { getWheelId } from "../../../../redux/selectors/segmentBuilderSelectors";
import { saveRecentMedia, saveExternalImage } from "../../../../services/api";
import { toast } from "react-toastify";

const Container = styled.div`
  display: flex;
  margin-top: 10px;
  flex-direction: column;
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorContainer = styled.div`
  display: flex;
  background-color: #fff4f5;
  padding: 5px;
  border-radius: 8px;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const DisclaimerContainer = styled.div`
  display: flex;
  background-color: #f79a001a;
  padding: 12px;
  border-radius: 8px;
  align-items: flex-start;
  gap: 10px;
  margin-top: 15px;
  border: 1px solid #f79a00;
`;

function getEmbedCode(url) {
  const cleanedUrl = url.replace(
    /<span[^>]*id="mce_marker"[^>]*>.*?<\/span>/g,
    ""
  );

  // Regex for YouTube (including Shorts)
  const youtubeRegex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^#\&\?]+)/;

  // Regex for Instagram Reels
  const reelsRegex =
    /(?:https?:\/\/)?(?:www\.)?instagram\.com\/reel\/([^#\&\?\/]+)/;

  // Match YouTube URLs
  const youtubeMatch = cleanedUrl.match(youtubeRegex);
  if (youtubeMatch && youtubeMatch[1]) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  // Match Instagram Reels URLs
  const reelsMatch = cleanedUrl.match(reelsRegex);
  if (reelsMatch && reelsMatch[1]) {
    return `https://www.instagram.com/reel/${reelsMatch[1]}/embed`;
  }

  // Return original URL if no match
  return url;
}

export default function EmbedURL({
  type = "image",
  onConfirm,
  defaultValue,
  pathType = "",
  media,
  showDisclaimer = false,
  onExternalChange = null,
}) {
  const [value, setValue] = useState(media?.originalSrc || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const wheelId = useSelector(getWheelId);
  const user_id = getLoggedInUser().id;

  const getUploadPath = () => {
    if (pathType === "profile") {
      return getUserProfilePath({ user_id });
    } else if (pathType === "bussinessBox") {
      return getBusinessBoxPath({ user_id });
    } else {
      return getWheelUploadPath({ user_id, wheel_id: wheelId });
    }
  };

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  function getDocumentType(url) {
    const urlParts = url.split("/");
    if (urlParts.includes("document")) {
      return "g-docs";
    } else if (urlParts.includes("spreadsheets")) {
      return "g-sheets";
    } else if (urlParts.includes("presentation")) {
      return "g-slides";
    } else if (urlParts.includes("forms")) {
      return "g-forms";
    } else if (url.includes("drive.google.com")) {
      return "g-drive";
    } else if (url.includes("sharepoint.com")) {
      return "onedrive";
    } else {
      return "";
    }
  }

  const isDocumentAccessible = async (url) => {
    try {
      setLoading(true);
      const response = await fetch(url, { method: "HEAD" });
      return response.status >= 200 && response.status < 300;
    } catch (err) {
      console.error("Error validating document URL:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getFileType = () => {
    switch (type) {
      case "images":
        return "0";
      case "image":
        return "0";
      case "video":
        return "1";
      case "documents":
        return "2";
      case "audio":
        return "3";
    }
  };

  const onConfirmHandler = async () => {
    setError("");
    let url = value;
    const uploadPath = getUploadPath();

    switch (type) {
      case "image":
        let file;
        if (isBase64Image(value)) file = base64ToFile(value);
        else {
          try {
            const base64String = await fetchImageAsBase64(url);
            file = base64ToFile(base64String, "Untitled_image");
          } catch (error) {
            //If the image is not accessible due to CORS, upload it via backend API
            try {
              const id = getRandomId();
              onConfirm({ id, loading: true });
              const saveResponse = await saveExternalImage({
                url: value,
                wheel_id: wheelId,
              });
              const uploadedUrl = saveResponse?.data?.data?.url;
              onConfirm({
                id,
                src: uploadedUrl,
                title: "",
                originalSrc: value,
                loading: false,
              });
              await saveRecentMedia(wheelId, {
                type: getFileType(),
                url: uploadedUrl,
              });
            } catch (uploadError) {
              console.error("Failed to upload external image:", uploadError);
              onConfirm({ id: getRandomId(), loading: false });
              toast.error(
                "Failed to upload image. Please try uploading directly."
              );
            }
            return;
          }
        }
        try {
          onConfirm({ id: getRandomId(), loading: true });
          const { location } = await handleFileUpload(file, uploadPath);
          url = location;
          onConfirm({
            src: url,
            title: "",
            originalSrc: value,
            loading: false,
          });
          await saveRecentMedia(wheelId, {
            type: getFileType(),
            url: url,
            metadata: {
              mimeType: file.type,
            },
          });
        } catch (error) {
          onConfirm({ id: getRandomId(), loading: false });
          toast.error(error);
        }

        break;

      case "video":
        url = getEmbedCode(value);
        onConfirm({ src: url, title: "Untitled video" });
        await saveRecentMedia(wheelId, {
          type: getFileType(),
          url,
          originalSrc: value,
        });
        break;

      case "images":
        onConfirm({
          id: getRandomId(),
          src: url,
          title: "",
          originalSrc: value,
        });
        await saveRecentMedia(wheelId, {
          type: getFileType(),
          url,
        });
        break;

      case "wheel":
        onConfirm(url);
        break;

      case "document":
      case "documents":
        if (!isDocumentValid(url)) {
          setError(
            "Only the following URL sources are accepted: Google Drive, Google Docs, Google Sheets, and One Drive."
          );
          return;
        }
        const id = getRandomId();
        onConfirm({ id, loading: true });

        if (await isDocumentAccessible(url)) {
          onConfirm({
            id,
            loading: false,
            src: url,
            title: getDocumentId(url),
            type: getDocumentType(url),
            originalSrc: value,
          });
          await saveRecentMedia(wheelId, {
            type: getFileType(),
            url,
            metadata: {
              title: getDocumentId(url),
              type: getDocumentType(url),
            },
          });
        } else {
          onConfirm({ id, loading: false });
          setError(
            "Invalid file URL - make sure the accessibility settings are set to 'Anyone with the Link' can ‘View', ‘Comment', or ‘Edit' "
          );
        }
        break;
    }
  };

  const getPlaceholderText = () => {
    switch (type) {
      case "image" || "images":
        return "Put an image URL below to upload your image directly!";
      case "video":
        return "Put a video URL below to upload your video directly!";
      case "wheel":
        return "Paste a URL below to upload the document directly!";
      case "document" || "documents":
        return "Put a google docs URL below to upload your document directly!";
      default:
        return "Put a URL below to upload your media directly!";
    }
  };

  return (
    <Container>
      <TextView>{getPlaceholderText()}</TextView>
      <Row>
        <Input
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (onExternalChange) onExternalChange(e.target.value);
          }}
          onBlur={(e) => {
            if (onExternalChange) onExternalChange(e.target.value);
          }}
          variant={"secondary"}
          containerStyle={{ width: "100%", marginBottom: 0, marginRight: 10 }}
          inputStyle={{
            borderColor: error && "#B5251F",
            color: error && "#B5251F",
          }}
        />

        <Button
          isLoading={loading}
          variant="primary"
          disabled={!value}
          style={{ width: 100, height: 36, fontSize: 12 }}
          onClick={onConfirmHandler}
        >
          Confirm
        </Button>
      </Row>
      {showDisclaimer && (
        <DisclaimerContainer>
          <TextView
            variant="largeText"
            color={"#565556"}
            style={{ fontSize: 14, lineHeight: 1.4 }}
          >
            <strong>Warning:</strong> Wheel Portraits and Wallpapers added
            through the Embed URL feature may not display correctly on social
            media. As an alternative, please download the image and upload it
            using the Upload Tab.
          </TextView>
        </DisclaimerContainer>
      )}
      {error && (
        <ErrorContainer>
          <ReportProblemIcon style={{ color: "#B5251F" }} />
          <TextView variant="tag" color={"#B5251F"} style={{ fontSize: 12 }}>
            {error}
          </TextView>
        </ErrorContainer>
      )}
    </Container>
  );
}
